/**
 * El carrito.
 *
 * Client Component: el carrito vive en el `localStorage` del visitante, así
 * que se arma en el cliente. El server renderiza la estructura (resumen,
 * notas de envío y garantía) para que la página llegue con contenido.
 *
 * El precio se calcula con el mismo formateador del servidor (`lib/format`),
 * con la moneda y el locale de la API: si la API cambia la moneda, el
 * carrito cambia con ella.
 */
'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

import { icons } from '@/components/icons';
import { precio } from '@/lib/format';
import { imagenDeProducto } from '@/lib/placeholders';
import type { Producto } from '@/lib/tipos';

export interface LineaCarrito {
  /** `slug` o `slug:idVariante`. */
  clave: string;
  slug: string;
  idVariante?: string;
  cantidad: number;
}

export interface Props {
  productos: Producto[];
  notaEnvio: string;
  etiquetaImpuestos: string;
  textoVacio: string;
  textoContinuar: string;
}

const CLAVE_CARRITO = 'joyeria.carrito';

function leerCarrito(): LineaCarrito[] {
  if (typeof window === 'undefined') return [];
  try {
    const crudo = window.localStorage.getItem(CLAVE_CARRITO);
    if (!crudo) return [];
    const datos = JSON.parse(crudo) as Record<string, number>;
    return Object.entries(datos).map(([clave, cantidad]) => {
      const [slug, idVariante] = clave.split(':');
      return { clave, slug, idVariante, cantidad };
    });
  } catch {
    return [];
  }
}

/** Reconstruye las líneas del carrito con los datos del catálogo. */
function resolverLineas(lineas: LineaCarrito[], productos: Producto[]) {
  return lineas
    .map((linea) => {
      const producto = productos.find((p) => p.slug === linea.slug);
      if (!producto) return null;
      const variante = linea.idVariante
        ? producto.variantes.find((v) => v.id === linea.idVariante)
        : null;
      const unitario = variante?.precio ?? producto.precio;
      return { ...linea, producto, variante, unitario, total: unitario * linea.cantidad };
    })
    .filter((linea): linea is NonNullable<typeof linea> => linea !== null);
}

export function CartView({ productos, notaEnvio, etiquetaImpuestos, textoVacio, textoContinuar }: Props) {
  const [lineas, setLineas] = useState<LineaCarrito[]>([]);
  const [listo, setListo] = useState(false);

  useEffect(() => {
    setLineas(leerCarrito());
    setListo(true);
  }, []);

  const resueltas = resolverLineas(lineas, productos);
  const subtotal = resueltas.reduce((acc, linea) => acc + linea.total, 0);

  function guardar(nuevas: LineaCarrito[]) {
    setLineas(nuevas);
    const datos: Record<string, number> = {};
    nuevas.forEach((linea) => {
      datos[linea.clave] = linea.cantidad;
    });
    try {
      window.localStorage.setItem(CLAVE_CARRITO, JSON.stringify(datos));
    } catch {
      /* si el storage está bloqueado, el carrito no persiste: no es fatal */
    }
    window.dispatchEvent(new CustomEvent('joyeria:carrito', { detail: datos }));
  }

  function cambiar(clave: string, delta: number) {
    const nuevas = leerCarrito()
      .map((linea) =>
        linea.clave === clave ? { ...linea, cantidad: Math.max(0, linea.cantidad + delta) } : linea,
      )
      .filter((linea) => linea.cantidad > 0);
    guardar(nuevas);
  }

  function quitar(clave: string) {
    guardar(leerCarrito().filter((linea) => linea.clave !== clave));
  }

  return (
    <div className="container cart-layout">
      <div>
        {listo && resueltas.length === 0 ? (
          <p className="empty-state">{textoVacio}</p>
        ) : (
          <div data-cart-lines>
            {resueltas.map((linea) => (
              <div className="cart-line" key={linea.clave}>
                <Link className="cart-line__media" href={`/productos/${linea.producto.slug}`}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={
                      imagenDeProducto(linea.producto)
                    }
                    alt={linea.producto.nombre}
                    width={120}
                    height={160}
                  />
                </Link>
                <div className="cart-line__info">
                  <p className="cart-line__name">{linea.producto.nombre}</p>
                  {linea.variante?.medida != null && (
                    <p className="cart-line__variant">Medida N° {linea.variante.medida}</p>
                  )}
                  {linea.variante?.largoCm != null && (
                    <p className="cart-line__variant">Largo {linea.variante.largoCm} cm</p>
                  )}
                  <p className="cart-line__price">{precio(linea.unitario)}</p>
                </div>
                <div className="qty">
                  <button
                    className="qty__btn"
                    type="button"
                    onClick={() => cambiar(linea.clave, -1)}
                    aria-label={`Restar una unidad de ${linea.producto.nombre}`}
                  >
                    {icons.menos}
                  </button>
                  <span className="qty__value">{linea.cantidad}</span>
                  <button
                    className="qty__btn"
                    type="button"
                    onClick={() => cambiar(linea.clave, 1)}
                    aria-label={`Sumar una unidad de ${linea.producto.nombre}`}
                  >
                    {icons.mas}
                  </button>
                </div>
                <p className="cart-line__total">{precio(linea.total)}</p>
                <button
                  className="cart-line__remove"
                  type="button"
                  onClick={() => quitar(linea.clave)}
                  aria-label={`Quitar ${linea.producto.nombre} del carrito`}
                >
                  Quitar
                </button>
              </div>
            ))}
          </div>
        )}

        <p style={{ marginTop: 18 }}>
          <Link className="section-link" href="/colecciones">
            Seguir comprando
          </Link>
        </p>
      </div>

      <aside className="summary" aria-labelledby="resumen-carrito">
        <p className="summary__title" id="resumen-carrito">
          Resumen
        </p>
        <div className="summary__row">
          <span>Subtotal</span>
          <span data-cart-subtotal>{precio(subtotal)}</span>
        </div>
        <div className="summary__row">
          <span>Envío</span>
          <span data-cart-shipping>A calcular</span>
        </div>
        <div className="summary__row summary__row--total">
          <span>Total</span>
          <span data-cart-total>{precio(subtotal)}</span>
        </div>
        <p className="summary__note">
          {etiquetaImpuestos}. {notaEnvio}
        </p>
        <Link
          className="btn btn--block"
          href="/checkout"
          style={{ marginTop: 16 }}
          data-cart-checkout
        >
          {textoContinuar}
        </Link>
      </aside>
    </div>
  );
}
